import { Suspense, useCallback, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import Loading from "components/Loading";
import SearchStatusConfirmModal from "components/SearchStatusConfirmModal";
import color from "constants/color";
import { orderDetailDisplayPage, OrderDetailDisplayPage } from "constants/orderDetailDisplayPage";
import useAcceptEstimate from "hooks/api/useAcceptEstimate";
import useFindEstimates from "hooks/api/useFindEstimates";
import useGetOrder from "hooks/api/useGetOrder";
import useRejectEstimate from "hooks/api/useRejectEstimate";
import useUpdateOrder from "hooks/api/useUpdateOrder";
import { useSyncEffect } from "hooks/effects";
import useErrorModalStatusContext from "hooks/useErrorModalStatusContext";
import { useInfoModal } from "hooks/useModal";
import { AcceptForm, Estimates, OrderEdit, RejectForm } from "pages/OrderDetail/orderDetailLazyPages";
import useOrderSearchStatusSwitch from "pages/OrderDetail/useOrderSearchStatusSwitch";
import useShareEstimatesActions from "pages/OrderDetail/useShareEstimatesActions";
import { isBrowser, isTablet } from "utils/deviceDetect";
import { sortForDisplay, updateEstimateStatus } from "utils/estimateUtils";

// パーツ定義
const Board = styled.div<{ isMobile?: boolean }>`
  height: 100%;
  width: 100%;
  margin: ${({ isMobile }) => (isTablet ? "24px" : !isMobile ? "24px 32px 32px" : "0")};
  display: flex;
  flex-direction: column;
  background-color: ${color.background};
`;
const LoadingWrap = styled.div`
  display: flex;
  height: 50vh;
  justify-content: center;
  align-items: center;
`;

// 共有画面の各コンポーネントがこのファイルから import しているため、移動後も同じ場所から参照できるようにする
export type { ShareEstimatesActionsType } from "pages/OrderDetail/useShareEstimatesActions";

type LocationState = Readonly<{
  redirectDisplayPage: OrderDetailDisplayPage;
  cancelRequestedEstimateId?: string;
}>;

const OrderDetail = () => {
  const location = useLocation();
  const state: LocationState = (location.state as LocationState) ?? {
    redirectDisplayPage: orderDetailDisplayPage.ESTIMATES,
  };
  const [displayPage, setDisplayPage] = useState<OrderDetailDisplayPage>(state.redirectDisplayPage);
  const params = useParams();
  const navigate = useNavigate();
  const { InfoModal, showInfoModal, hideInfoModal } = useInfoModal();
  const { updateOrder } = useUpdateOrder();
  const { acceptEstimate } = useAcceptEstimate();
  const { rejectEstimate } = useRejectEstimate();

  const { order: orderForDisplay, error: orderError, mutate: setOrderForDisplay } = useGetOrder(params.id ?? "");

  const { estimates, error: estimateError, mutate: setEstimates, isValidating } = useFindEstimates(params.id ?? "");
  const {
    shareEstimatesStep,
    setShareEstimatesStepResetScroll,
    shareEstimates,
    setShareEstimates,
    shareEstimatesActions,
    onClickShare,
  } = useShareEstimatesActions(estimates);

  const [orderForEdit, setOrderForEdit] = useState<Order | null>(null);
  const [infoModalTitle, setInfoModalTitle] = useState<string>("");
  const [estimateId, setEstimateId] = useState("");
  const [estimateForAcceptOrReject, setEstimateForAcceptOrReject] = useState<Estimate | null>(null);
  const [cancelRequestedEstimateIds, setCancelRequestedEstimateIds] = useState<string[]>([]);

  useSyncEffect(() => {
    const state = location.state as LocationState | null;
    const id = state?.cancelRequestedEstimateId;
    if (id) {
      setCancelRequestedEstimateIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    }
  }, [location.state]);

  const allEstimates = sortForDisplay(estimates);

  const setOrder = useCallback(async () => {
    setOrderForEdit(orderForDisplay);
  }, [orderForDisplay, setOrderForEdit]);

  const toOrderPage = useCallback(() => {
    navigate("/order");
  }, [navigate]);

  const { showErrorModal } = useErrorModalStatusContext();

  useSyncEffect(() => {
    if (orderError || estimateError) showErrorModal({ httpMethod: "get", onPostClose: toOrderPage });
    setOrder();
  }, [estimateError, navigate, orderError, setOrder, showErrorModal, toOrderPage]);

  const accept = useCallback(
    async (userRequest: string) => {
      if (!orderForDisplay?.id) return;
      const status = await acceptEstimate(orderForDisplay.id, estimateId, userRequest).catch(() =>
        showErrorModal({ httpMethod: "post" })
      );
      if (status === 200) {
        setEstimates(updateEstimateStatus(estimates, estimateId, "accepted"));
        setInfoModalTitle("契約を依頼しました");
        showInfoModal();
      }
    },
    [orderForDisplay, acceptEstimate, estimateId, setEstimates, estimates, showInfoModal, showErrorModal]
  );

  const reject = useCallback(
    async (rejectReason: RejectReason) => {
      if (!orderForDisplay?.id) return;
      const status = await rejectEstimate(orderForDisplay.id, estimateId, rejectReason).catch(() =>
        showErrorModal({ httpMethod: "post" })
      );
      if (status === 200) {
        setEstimates(updateEstimateStatus(estimates, estimateId, "rejected"));
        // 見送りと同時に検索を停止した場合は、カードの検索ステータスを取り直す
        if (rejectReason.stopSearch) await setOrderForDisplay();
        setInfoModalTitle("契約を見送りました");
        showInfoModal();
      }
    },
    [
      orderForDisplay,
      rejectEstimate,
      estimateId,
      setEstimates,
      estimates,
      setOrderForDisplay,
      showInfoModal,
      showErrorModal,
    ]
  );

  const showCompletion = useCallback(
    (message: string) => {
      setInfoModalTitle(message);
      showInfoModal();
    },
    [showInfoModal]
  );
  const { isConfirmModalOpen, openConfirmModal, closeConfirmModal, switchSearchStatus } = useOrderSearchStatusSwitch(
    orderForDisplay,
    setOrderForDisplay,
    showCompletion
  );

  const update = useCallback(
    async (order: Order) => {
      if (!order?.id) return;
      const status = await updateOrder(order).catch(() => showErrorModal({ httpMethod: "put" }));
      if (status === 200) {
        setOrderForDisplay(order);
        setInfoModalTitle("依頼内容を変更しました");
        showInfoModal();
      }
    },
    [updateOrder, setOrderForDisplay, showInfoModal, showErrorModal]
  );

  const onClickReject = (estimateId: string) => {
    setEstimateId(estimateId);
    const estimate = estimates.find((e) => e.id === estimateId);
    if (estimate) {
      setEstimateForAcceptOrReject(estimate);
    }
    setDisplayPage(orderDetailDisplayPage.REJECT_ESTIMATE);
  };

  const onClickAccept = (estimateId: string) => {
    setEstimateId(estimateId);
    const estimate = estimates.find((e) => e.id === estimateId);
    if (estimate) {
      setEstimateForAcceptOrReject(estimate);
    }
    setDisplayPage(orderDetailDisplayPage.ACCEPT_ESTIMATE);
  };

  const onClickCancelRequest = (estimateId: string) => {
    navigate(`/order/${params.id ?? ""}/estimate/${estimateId}/cancel-request`);
  };

  const handleAccept = async (userRequest: string) => {
    await accept(userRequest);
    setEstimateId("");
  };
  const handleReject = async (rejectReason: RejectReason) => {
    await reject(rejectReason);
    setEstimateId("");
  };

  const handleComplete = () => {
    hideInfoModal();
    if (displayPage === orderDetailDisplayPage.EDIT_ORDER) return toOrderPage();
    setDisplayPage(orderDetailDisplayPage.ESTIMATES);
  };

  const canAccept = (estimateId: string) => {
    const estimate = estimates.find((e) => e.id === estimateId);
    if (estimate == null) return false;
    return estimate.status === "unanswered" || estimate.status === "rejected";
  };

  const canReject = (estimateId: string) => {
    const estimate = estimates.find((e) => e.id === estimateId);
    if (estimate == null) return false;
    return estimate.status === "unanswered";
  };

  return (
    <>
      <Board isMobile={!isBrowser && !isTablet}>
        {displayPage === orderDetailDisplayPage.ESTIMATES && orderForDisplay && estimates && !isValidating && (
          <Suspense fallback={<Loading />}>
            <Estimates
              order={orderForDisplay}
              estimates={allEstimates}
              editHandler={() => setDisplayPage(orderDetailDisplayPage.EDIT_ORDER)}
              acceptHandler={onClickAccept}
              rejectHandler={onClickReject}
              onClickCancelRequest={onClickCancelRequest}
              toOrderList={toOrderPage}
              shareEstimatesStep={shareEstimatesStep}
              setShareEstimatesStep={setShareEstimatesStepResetScroll}
              shareEstimates={shareEstimates}
              setShareEstimates={setShareEstimates}
              shareEstimatesActions={shareEstimatesActions}
              onClickShare={onClickShare}
              onClickSearchStatusSwitch={openConfirmModal}
            />
          </Suspense>
        )}
        {displayPage === orderDetailDisplayPage.EDIT_ORDER && orderForEdit && (
          <Suspense fallback={<Loading />}>
            <OrderEdit order={orderForEdit} onClose={toOrderPage} submit={update} />
          </Suspense>
        )}
        {displayPage === orderDetailDisplayPage.REJECT_ESTIMATE &&
          estimateForAcceptOrReject &&
          orderForDisplay.location && (
            <Suspense fallback={<Loading />}>
              <RejectForm
                estimate={estimateForAcceptOrReject}
                orderLocation={orderForDisplay.location}
                rejectHandler={handleReject}
                cancelHandler={() => setDisplayPage(orderDetailDisplayPage.ESTIMATES)}
                canReject={canReject}
                searchStatus={orderForDisplay.searchStatus}
                onClickSearchStatusSwitch={openConfirmModal}
              />
            </Suspense>
          )}
        {displayPage === orderDetailDisplayPage.ACCEPT_ESTIMATE &&
          estimateForAcceptOrReject &&
          orderForDisplay.location && (
            <Suspense fallback={<Loading />}>
              <AcceptForm
                estimate={estimateForAcceptOrReject}
                orderLocation={orderForDisplay.location}
                acceptHandler={handleAccept}
                onClick={() => setDisplayPage(orderDetailDisplayPage.ESTIMATES)}
                canAccept={canAccept}
              />
            </Suspense>
          )}
        {!orderForDisplay ||
          (!estimates && (
            <LoadingWrap>
              <Loading />
            </LoadingWrap>
          ))}
      </Board>
      <SearchStatusConfirmModal
        searchStatus={orderForDisplay?.searchStatus}
        isVisible={isConfirmModalOpen}
        onClose={closeConfirmModal}
        onConfirm={switchSearchStatus}
      />
      <InfoModal title={infoModalTitle} onClick={handleComplete} />
    </>
  );
};

export default OrderDetail;
